import { useEffect, useRef, useState } from 'react'

/** How long an item is kept on screen after it leaves the data. */
export const EXIT_MS = 220

/**
 * A mass removal — emptying the trash, deleting a folder — is not the gesture
 * this animation is for, and playing it hundreds of times at once looks broken.
 */
export const MAX_ANIMATED = 8

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export interface Departing { departing?: boolean }

interface Ghost<T> { item: T; at: number }

/**
 * Keeps items that have just left `items` on screen for `ms`, in the position
 * they held, flagged so they can be animated out.
 *
 * This watches the data rather than hooking each delete button, so a note
 * removed by the keyboard, the row menu, a swipe, the editor or another device
 * all leave the same way — and the write itself is never delayed for the sake
 * of the animation.
 *
 * `resetKey` identifies the list being shown. Switching folders or typing in
 * the search box replaces the contents wholesale; that is a different list, not
 * a deletion, so nothing animates.
 */
export function useDeparting<T extends { id: string }>(
  items: T[],
  resetKey: string,
  ms = EXIT_MS,
): Array<T & Departing> {
  const [ghosts, setGhosts] = useState<Array<Ghost<T>>>([])
  const prev = useRef<T[]>(items)
  const key = useRef(resetKey)
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const live = new Set(items.map((i) => i.id))

  // Reconciled during render — React's supported way to derive state from new
  // props. Deferring it to an effect would paint one frame with the row gone,
  // which is the jump this whole hook exists to remove.
  if (key.current !== resetKey) {
    key.current = resetKey
    if (ghosts.length) setGhosts([])
  } else {
    const held = new Set(ghosts.map((g) => g.item.id))
    const gone = prev.current.filter((p) => !live.has(p.id) && !held.has(p.id))
    // An id that is live again — an undo, or a note restored from the trash —
    // must drop its ghost the moment it returns, not merely be skipped while
    // rendering. A ghost left in place still counts as `held`, so deleting
    // that note a second time would reuse the position it had the first time.
    const revived = ghosts.some((g) => live.has(g.item.id))
    const animate = gone.length > 0 && gone.length <= MAX_ANIMATED && !prefersReducedMotion()

    if (animate || revived) {
      setGhosts([
        ...ghosts.filter((g) => !live.has(g.item.id)),
        ...(animate
          ? gone.map((item) => ({ item, at: prev.current.findIndex((p) => p.id === item.id) }))
          : []),
      ])
    }
  }
  prev.current = items

  useEffect(() => {
    for (const { item } of ghosts) {
      if (timers.current.has(item.id)) continue
      timers.current.set(item.id, setTimeout(() => {
        timers.current.delete(item.id)
        setGhosts((g) => (g.some((x) => x.item.id === item.id)
          ? g.filter((x) => x.item.id !== item.id)
          : g))
      }, ms))
    }
  }, [ghosts, ms])

  useEffect(() => {
    const running = timers.current
    return () => { running.forEach((t) => clearTimeout(t)); running.clear() }
  }, [])

  if (!ghosts.length) return items

  const out: Array<T & Departing> = [...items]
  // Front to back: each insertion restores one slot, so by the time a later
  // index is used the array has regained everything that preceded it.
  for (const { item, at } of [...ghosts].sort((a, b) => a.at - b.at)) {
    if (live.has(item.id)) continue
    out.splice(Math.min(Math.max(at, 0), out.length), 0, { ...item, departing: true })
  }
  return out
}
